import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { products } from "../data/productsData";
import { FaPhoneAlt, FaWhatsapp, FaArrowLeft, FaCheckCircle, FaMapMarkerAlt, FaEnvelope, FaChevronRight } from "react-icons/fa";

export default function ProductDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const product = products.find((p) => p.slug === slug);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        if (product) {
            document.title = product.metaTitle;
            // Update meta description
            let meta = document.querySelector('meta[name="description"]');
            if (!meta) {
                meta = document.createElement("meta");
                meta.name = "description";
                document.head.appendChild(meta);
            }
            meta.content = product.metaDescription;
        }
    }, [product, slug]);

    if (!product) {
        return (
            <div className="pd-not-found">
                <h1>Product not found</h1>
                <Link to="/" className="pd-back-btn">← Back to Home</Link>
            </div>
        );
    }

    const currentIndex = products.findIndex((p) => p.slug === slug);
    const prevProduct = currentIndex > 0 ? products[currentIndex - 1] : null;
    const nextProduct = currentIndex < products.length - 1 ? products[currentIndex + 1] : null;

    return (
        <div className="product-detail-page">
            {/* ── HERO BANNER ── */}
            <section className="pd-hero">
                <div className="pd-hero__overlay" />
                <div className="pd-hero__content">
                    <Link to="/#services" className="pd-back-btn">
                        <FaArrowLeft /> Back to Products
                    </Link>
                    <p className="pd-hero__tagline">{product.tagline}</p>
                    <h1 className="pd-hero__title">{product.heroTitle}</h1>
                    <p className="pd-hero__subtitle">{product.heroSubtitle}</p>
                    <div className="pd-hero__cta-group">
                        <a href="tel:9716043891" className="pd-cta-btn pd-cta-btn--primary" id="pd-call-btn">
                            <FaPhoneAlt /> Call Now: 9716043891
                        </a>
                        <a href="https://wa.me/919716043891" target="_blank" rel="noreferrer" className="pd-cta-btn pd-cta-btn--whatsapp" id="pd-whatsapp-btn">
                            <FaWhatsapp /> WhatsApp Us
                        </a>
                    </div>
                </div>
            </section>


            {/* ── MAIN CONTENT WRAPPER ── */}
            <div className="pd-container">

                {/* INTRO SECTION */}
                <section className="pd-section pd-intro">
                    <div className="pd-section__inner">
                        <h2 className="pd-section__heading">{product.intro.heading}</h2>
                        <p className="pd-intro__description">{product.intro.description}</p>
                        <div className="pd-searching-for">
                            <h3>If you are searching for:</h3>
                            <ul>
                                {product.intro.searchingFor.map((item, i) => (
                                    <li key={i}>
                                        <FaCheckCircle className="pd-check-icon" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                            <p className="pd-intro__cta-note">
                                <strong>S.N.S. Glass House</strong> is your trusted solution.{" "}
                                <a href="tel:9716043891">📞 Call / WhatsApp: 9716043891</a>
                            </p>
                        </div>
                    </div>
                </section>

                {/* WHAT IS SECTION */}
                <section className="pd-section pd-what-is">
                    <div className="pd-section__inner">
                        <h2 className="pd-section__heading pd-section__heading--icon">
                            🔎 {product.whatIs.heading}
                        </h2>
                        <p className="pd-what-is__description">{product.whatIs.description}</p>
                        <div className="pd-tags">
                            {product.whatIs.points.map((pt, i) => (
                                <span key={i} className="pd-tag">{pt}</span>
                            ))}
                        </div>
                    </div>
                </section>

                {/* APPLICATIONS SECTION */}
                <section className="pd-section pd-applications">
                    <div className="pd-section__inner">
                        <h2 className="pd-section__heading">🏠 Applications & Use Cases</h2>
                        <div className="pd-app-grid">
                            {product.applications.map((app, i) => (
                                <div key={i} className="pd-app-card">
                                    <div className="pd-app-card__number">{String(i + 1).padStart(2, "0")}</div>
                                    <h3 className="pd-app-card__title">{app.title}</h3>
                                    <ul className="pd-app-card__list">
                                        {app.points.map((pt, j) => (
                                            <li key={j}><FaChevronRight className="pd-chevron-icon" />{pt}</li>
                                        ))}
                                    </ul>
                                    {app.note && <p className="pd-app-card__note">{app.note}</p>}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* WHY CHOOSE + PRICING SIDE BY SIDE */}
                <div className="pd-two-col">
                    {/* WHY CHOOSE */}
                    <section className="pd-section pd-why-choose">
                        <div className="pd-section__inner">
                            <h2 className="pd-section__heading">🏆 Why Choose S.N.S. Glass House?</h2>
                            <ul className="pd-why-list">
                                {product.whyChoose.map((item, i) => (
                                    <li key={i}>
                                        <FaCheckCircle className="pd-check-icon pd-check-icon--gold" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </section>

                    {/* PRICING */}
                    <section className="pd-section pd-pricing">
                        <div className="pd-section__inner">
                            <h2 className="pd-section__heading">💰 Pricing & Quotation</h2>
                            <p className="pd-pricing__note">{product.pricingNote}</p>
                            <a href="tel:9716043891" className="pd-cta-btn pd-cta-btn--primary pd-pricing__cta" id="pd-price-call-btn">
                                <FaPhoneAlt /> Get Free Quote
                            </a>
                        </div>
                    </section>
                </div>

                {/* INSTALLATION PROCESS */}
                <section className="pd-section pd-process">
                    <div className="pd-section__inner">
                        <h2 className="pd-section__heading">⚙️ Our Installation Process</h2>
                        <div className="pd-process-steps">
                            {["Site Measurement & Visit", "Design Selection", "Material Cutting & Preparation", "Professional Installation", "Final Alignment & Finishing"].map((step, i) => (
                                <div key={i} className="pd-process-step">
                                    <div className="pd-process-step__num">{i + 1}</div>
                                    <p>{step}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* FAQ SECTION */}
                <section className="pd-section pd-faq">
                    <div className="pd-section__inner">
                        <h2 className="pd-section__heading">❓ Frequently Asked Questions</h2>
                        <div className="pd-faq-list">
                            {product.faqs.map((faq, i) => (
                                <details key={i} className="pd-faq-item">
                                    <summary className="pd-faq-item__q">
                                        <span>{faq.q}</span>
                                        <span className="pd-faq-item__chevron">›</span>
                                    </summary>
                                    <p className="pd-faq-item__a">{faq.a}</p>
                                </details>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CONTACT CTA BANNER */}
                <section className="pd-section pd-contact-cta">
                    <div className="pd-contact-cta__inner">
                        <h2>Ready to Get Started?</h2>
                        <p>
                            Contact S.N.S. Glass House for a free site visit & quotation in Chhatarpur, Vasant Kunj & South Delhi.
                        </p>
                        <div className="pd-contact-cta__details">
                            <a href="tel:9716043891" className="pd-contact-item">
                                <FaPhoneAlt /> 9716043891
                            </a>
                            <a href="mailto:teamsnsglasshouse@gmail.com" className="pd-contact-item">
                                <FaEnvelope /> teamsnsglasshouse@gmail.com
                            </a>
                            <span className="pd-contact-item">
                                <FaMapMarkerAlt /> Chhatarpur, South Delhi
                            </span>
                        </div>
                        <div className="pd-contact-cta__btns">
                            <a href="tel:9716043891" className="pd-cta-btn pd-cta-btn--primary" id="pd-footer-call-btn">
                                <FaPhoneAlt /> Call Now
                            </a>
                            <a href="https://wa.me/919716043891" target="_blank" rel="noreferrer" className="pd-cta-btn pd-cta-btn--whatsapp" id="pd-footer-whatsapp-btn">
                                <FaWhatsapp /> WhatsApp
                            </a>
                        </div>
                    </div>
                </section>

                {/* RELATED PRODUCTS NAV */}
                <section className="pd-section pd-related">
                    <div className="pd-section__inner">
                        <h2 className="pd-section__heading">Explore Other Products</h2>
                        <div className="pd-related-grid">
                            {products
                                .filter((p) => p.slug !== slug)
                                .slice(0, 4)
                                .map((p) => (
                                    <Link key={p.id} to={`/products/${p.slug}`} className="pd-related-card">
                                        <span className="pd-related-card__label">{p.navLabel}</span>
                                        <FaChevronRight />
                                    </Link>
                                ))}
                        </div>
                    </div>
                    {/* Prev / Next navigation */}
                    <div className="pd-prev-next">
                        {prevProduct ? (
                            <button className="pd-nav-btn pd-nav-btn--prev" onClick={() => navigate(`/products/${prevProduct.slug}`)}>
                                <FaArrowLeft /> {prevProduct.navLabel}
                            </button>
                        ) : <div />}
                        {nextProduct && (
                            <button className="pd-nav-btn pd-nav-btn--next" onClick={() => navigate(`/products/${nextProduct.slug}`)}>
                                {nextProduct.navLabel} <FaChevronRight />
                            </button>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
}
